import { getApiBaseUrl } from '../../../utils/apiConfig';
import { formatReincidenciaDataAbertura } from '../../board/utils/orderDetailsReincidenciaUtils';
import { formatDateToPTBR, formatNumber } from './formatters';

const API_BASE_URL = getApiBaseUrl();

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Mesmo default do EquipmentConsultDetailModal (últimos 6 meses). */
function getDefaultProductionPeriod() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 6);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

const PRODUCAO_META = [
  { key: 'pb', label: 'A4 P&B', cor: '#7c4dff', fill: 'rgba(124,77,255,.35)', field: 'TB02117_TOTPB', medField: 'TB02117_MEDATUAL' },
  { key: 'color', label: 'A4 Color', cor: '#06b6d4', fill: 'rgba(6,182,212,.35)', field: 'TB02117_TOTCOLOR', medField: 'TB02117_MEDATUALC' },
  { key: 'dig', label: 'Digitalização', cor: '#10b981', fill: 'rgba(16,185,129,.35)', field: 'TB02117_TOTDG', medField: 'TB02117_MEDATUALDG' },
  { key: 'a3pb', label: 'A3 P&B', cor: '#d99320', fill: 'rgba(217,147,32,.30)', field: 'TB02117_TOTGF', medField: 'TB02117_MEDATUALGF' },
  { key: 'a3color', label: 'A3 Color', cor: '#d1544a', fill: 'rgba(209,84,74,.30)', field: 'TB02117_TOTGFC', medField: 'TB02117_MEDATUALGFC' },
];

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function parseBrDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const s = String(value).trim();
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) {
    const d = new Date(+br[3], +br[2] - 1, +br[1]);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatBr(value) {
  if (!value) return '—';
  const br = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(br)) return br;
  try {
    return formatDateToPTBR(value) || '—';
  } catch {
    return '—';
  }
}

function monthsBetween(from, to) {
  let months =
    (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  return Math.max(0, months);
}

/** Ex.: "3 anos e 6 meses" */
export function formatInstalledDuration(fromDate) {
  if (!fromDate) return '—';
  const months = monthsBetween(fromDate, new Date());
  if (months <= 0) return 'menos de 1 mês';
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [];
  if (y) parts.push(y === 1 ? '1 ano' : `${y} anos`);
  if (m) parts.push(m === 1 ? '1 mês' : `${m} meses`);
  return parts.join(' e ') || '—';
}

/** Ex.: "há 3 meses" */
export function formatAgoPt(fromDate) {
  if (!fromDate) return '—';
  const now = new Date();
  const months = monthsBetween(fromDate, now);
  if (months < 1) {
    const days = Math.floor((now.getTime() - fromDate.getTime()) / 86400000);
    if (days <= 0) return 'hoje';
    if (days === 1) return 'há 1 dia';
    return `há ${days} dias`;
  }
  if (months < 12) return months === 1 ? 'há 1 mês' : `há ${months} meses`;
  const y = Math.floor(months / 12);
  const rem = months % 12;
  if (rem === 0) return y === 1 ? 'há 1 ano' : `há ${y} anos`;
  return y === 1 ? `há 1 ano e ${rem} ${rem === 1 ? 'mês' : 'meses'}` : `há ${y} anos`;
}

function mapTipoOs(tipoRaw) {
  const t = String(tipoRaw || '')
    .trim()
    .toUpperCase();
  if (t.includes('PREVENT')) return 'Preventiva';
  if (t.includes('CORRET')) return 'Corretiva';
  if (!t) return '—';
  return t.charAt(0) + t.slice(1).toLowerCase();
}

function isCorretiva(tipo) {
  return mapTipoOs(tipo) === 'Corretiva';
}

function isPreventiva(tipo) {
  return mapTipoOs(tipo) === 'Preventiva';
}

function monthLabel(d) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${mm}/${d.getFullYear()}`;
}

async function fetchJson(path) {
  const response = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

function mapOrdens(rawList, pecasByOs, cutoff) {
  const rows = [];
  for (const o of rawList || []) {
    const aberturaRaw = o.TB02115_DATA;
    const aberturaDate = parseBrDate(aberturaRaw);
    if (!aberturaDate || aberturaDate < cutoff) continue;

    const numero = String(o.TB02115_CODIGO || '').trim();
    const pecas = (pecasByOs[numero] || []).map((p) => ({
      codigo: p.codigo,
      referencia: p.referencia,
      produto: p.nome,
      qtde: p.qtde,
    }));

    rows.push({
      numero: numero || '—',
      reincidencia: String(o.TB02115_OS || '').trim(),
      tipo: mapTipoOs(o.TIPO_OS),
      abertura: formatBr(aberturaRaw),
      aberturaImpressao: formatReincidenciaDataAbertura(aberturaRaw),
      aberturaDate,
      defeito: String(o.DEFEITO || '').trim() || 'Sem defeito',
      defeitoAlert: String(o.defeito_alert || o.DEFEITO_ALERT || '').trim(),
      colorBg: String(o.color_bg || o.COLOR_BG || '').trim(),
      tecnico: String(o.TB01024_NOME || '—').trim() || '—',
      condicao: String(o.TB01055_NOME || '—').trim() || '—',
      fechamento: formatBr(o.TB02122_DATAFIM),
      cliente: String(o.TB01008_NOME || '—').trim() || '—',
      contadores: [
        Number(o.TB02122_CONTADOR) || 0,
        Number(o.TB02122_CONTADORC) || 0,
        Number(o.TB02122_CONTADORDG) || 0,
        Number(o.TB02122_CONTADORGF) || 0,
        Number(o.TB02122_CONTADORGFC) || 0,
      ],
      motivo: String(o.TB02115_NOME || '—').trim() || '—',
      laudo: String(o.TB02122_OBS || '—').trim() || '—',
      pecas,
      temPecaFlag: !!(o.PECA_TROCADA || pecas.length),
      returnCount: Number(o.returnCount) || 0,
      ciclos: (o.cycles || []).map((c) => ({
        cycle: Number(c.cycle) || 0,
        label: c.label,
        condicao: String(c.condicao || '—').trim() || '—',
        tecnico: String(c.tecnico || '—').trim() || '—',
        fechamento: formatBr(c.fechamento),
        contadorPB: Number(c.contadorPB) || 0,
        laudo: String(c.laudo || '').trim(),
        pecas: !!c.pecas,
      })),
    });
  }
  rows.sort((a, b) => b.aberturaDate - a.aberturaDate);
  return rows;
}

function mapPecas(rawList, cutoff) {
  const rows = [];
  for (const p of rawList || []) {
    const dataRaw = p.TB02021_DATA;
    const dataDate = parseBrDate(dataRaw);
    if (dataDate && dataDate < cutoff) continue;
    const vidaNum = parseInt(p.TB01010_COPIA || 0, 10);
    rows.push({
      pedido: String(p.TB02022_CODIGO || '—'),
      data: formatBr(dataRaw),
      dataDate: dataDate || new Date(0),
      codigo: String(p.TB02022_PRODUTO || '—'),
      referencia: String(p.TB01010_REFERENCIA || '—'),
      nome: String(p.TB01010_NOME || '—'),
      qtde: parseInt(p.TB02022_QTPROD || 0, 10) || 0,
      os: String(p.TB02021_OS || '—'),
      vidaUtil: vidaNum > 0 ? `${formatNumber(vidaNum)} pág.` : '—',
      contadores: [
        Number(p.TB02021_CONTPB) || 0,
        Number(p.TB02021_CONTCOLOR) || 0,
        Number(p.TB02021_CONTTOTAL) || 0,
      ],
    });
  }
  rows.sort((a, b) => b.dataDate - a.dataDate);
  return rows;
}

function buildPareto(ordens) {
  const counts = new Map();
  for (const o of ordens) {
    const name = String(o.defeito || '').trim();
    if (!name || name === '—') continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([defeito, qtd]) => ({ defeito, qtd }))
    .sort((a, b) => b.qtd - a.qtd);
}

function buildProduction(productionRows) {
  const sorted = [...(productionRows || [])]
    .map((row) => {
      const d = parseBrDate(row.TB02117_DATA);
      return d ? { d, row } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.d - b.d);

  if (!sorted.length) {
    return {
      meses: [],
      series: PRODUCAO_META.map((m) => ({ ...m, valores: [], medAtual: [] })),
    };
  }

  // Agrega por mês (MM/yyyy via TB02117_DATA) para o eixo X
  const buckets = new Map();
  for (const item of sorted) {
    const key = `${item.d.getFullYear()}-${String(item.d.getMonth() + 1).padStart(2, '0')}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        d: new Date(item.d.getFullYear(), item.d.getMonth(), 1),
        label: monthLabel(item.d),
        TB02117_TOTPB: 0,
        TB02117_TOTCOLOR: 0,
        TB02117_TOTDG: 0,
        TB02117_TOTGF: 0,
        TB02117_TOTGFC: 0,
        TB02117_MEDATUAL: null,
        TB02117_MEDATUALC: null,
        TB02117_MEDATUALDG: null,
        TB02117_MEDATUALGF: null,
        TB02117_MEDATUALGFC: null,
      };
      buckets.set(key, bucket);
    }
    bucket.TB02117_TOTPB += Number(item.row.TB02117_TOTPB) || 0;
    bucket.TB02117_TOTCOLOR += Number(item.row.TB02117_TOTCOLOR) || 0;
    bucket.TB02117_TOTDG += Number(item.row.TB02117_TOTDG) || 0;
    bucket.TB02117_TOTGF += Number(item.row.TB02117_TOTGF) || 0;
    bucket.TB02117_TOTGFC += Number(item.row.TB02117_TOTGFC) || 0;
    // MEDATUAL: última leitura do mês (sorted asc → sobrescreve)
    for (const m of PRODUCAO_META) {
      if (!m.medField) continue;
      const raw = item.row[m.medField];
      if (raw == null || raw === '') continue;
      const n = Number(raw);
      if (!Number.isNaN(n)) bucket[m.medField] = n;
    }
  }

  const months = [...buckets.values()].sort((a, b) => a.d - b.d);
  const meses = months.map((m) => m.label);
  const series = PRODUCAO_META.map((m) => ({
    key: m.key,
    label: m.label,
    cor: m.cor,
    fill: m.fill,
    valores: months.map((row) => (m.field ? Number(row[m.field]) || 0 : 0)),
    medAtual: months.map((row) => {
      if (!m.medField) return null;
      const v = row[m.medField];
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    }),
  }));
  return { meses, series };
}

/**
 * Monta payload do painel Histórico do equipamento (mock Abrir OS v2).
 * @param {string} serie
 * @param {{ installDate?: string|Date|null, months?: number }} [opts]
 */
export async function fetchOpenOsV2History(serie, opts = {}) {
  const serieClean = String(serie || '')
    .trim()
    .toUpperCase();
  if (!serieClean) return null;

  const monthsRaw = Number(opts.months);
  const months = [3, 6, 12, 24].includes(monthsRaw) ? monthsRaw : 24;

  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setMonth(cutoff.getMonth() - months);

  const period = getDefaultProductionPeriod();

  const [ordensRes, pecasRes, consultRes] = await Promise.all([
    fetchJson(`/api/oficina/ordens-serie?serie=${encodeURIComponent(serieClean)}`).catch(() => ({
      data: [],
    })),
    fetchJson(`/api/oficina/pecas-trocadas?serie=${encodeURIComponent(serieClean)}`).catch(() => ({
      data: [],
    })),
    fetchJson(
      `/api/equipment/consult?serie=${encodeURIComponent(serieClean)}&dataInicio=${encodeURIComponent(period.startDate)}&dataFim=${encodeURIComponent(period.endDate)}`
    ).catch(() => ({ production: [] })),
  ]);

  const pecasRaw = Array.isArray(pecasRes.data) ? pecasRes.data : [];
  const pecasAll = mapPecas(pecasRaw, new Date(0));
  const pecasByOs = {};
  for (const p of pecasAll) {
    const os = String(p.os || '').trim();
    if (!os || os === '—') continue;
    if (!pecasByOs[os]) pecasByOs[os] = [];
    pecasByOs[os].push(p);
  }

  const ordensRaw = Array.isArray(ordensRes.data) ? ordensRes.data : [];
  const ordens = mapOrdens(ordensRaw, pecasByOs, cutoff);
  const pecas = mapPecas(pecasRaw, cutoff);

  const corretivasList = ordens.filter((o) => isCorretiva(o.tipo));
  const preventivasList = ordens.filter((o) => isPreventiva(o.tipo));
  const corretivas = corretivasList.length;
  const preventivas = preventivasList.length;
  const totalChamados = corretivas + preventivas;

  const lastCorr = corretivasList[0] || null;
  const lastPrev = preventivasList[0] || null;

  const installDate =
    parseBrDate(opts.installDate) ||
    parseBrDate(consultRes?.contract?.TB02112_DATA) ||
    null;

  const prod = buildProduction(
    Array.isArray(consultRes.production) ? consultRes.production : []
  );

  /** Ciclo mensal do modelo (TB01010_COPIA) — mesmo campo da Consulta de equipamento. */
  const copiaRaw = consultRes?.contract?.TB01010_COPIA;
  let cicloMensal = null;
  if (copiaRaw != null && copiaRaw !== '') {
    const n = Math.trunc(Number(copiaRaw));
    if (!Number.isNaN(n) && n > 0) cicloMensal = n;
  }

  return {
    tempoInstalado: formatInstalledDuration(installDate),
    dataInstalacao: installDate ? formatBr(installDate) : '—',
    periodo: `últimos ${months} meses`,
    periodMonths: months,
    totalChamados,
    corretivas,
    preventivas,
    ultimaCorretiva: lastCorr ? formatAgoPt(lastCorr.aberturaDate) : '—',
    ultimaCorretivaData: lastCorr ? lastCorr.abertura : '—',
    ultimaCorretivaOS: lastCorr ? lastCorr.numero : '—',
    ultimaPreventiva: lastPrev ? formatAgoPt(lastPrev.aberturaDate) : '—',
    ultimaPreventivaData: lastPrev ? lastPrev.abertura : '—',
    ultimaPreventivaOS: lastPrev ? lastPrev.numero : '—',
    paretoRaw: buildPareto(ordens),
    ordens,
    pecas,
    producaoMeses: prod.meses,
    producaoSeries: prod.series,
    cicloMensal,
  };
}

export function installOpenOsV2HistoryBridge() {
  window.__openOsV2FetchHistory = async (serie, installDateOrOpts, monthsMaybe) => {
    try {
      let opts = {};
      if (
        installDateOrOpts &&
        typeof installDateOrOpts === 'object' &&
        !(installDateOrOpts instanceof Date)
      ) {
        opts = { ...installDateOrOpts };
      } else {
        opts = { installDate: installDateOrOpts };
      }
      if (monthsMaybe != null && opts.months == null) opts.months = monthsMaybe;
      return await fetchOpenOsV2History(serie, opts);
    } catch {
      return null;
    }
  };
}

export function uninstallOpenOsV2HistoryBridge() {
  try {
    delete window.__openOsV2FetchHistory;
  } catch {
    window.__openOsV2FetchHistory = undefined;
  }
}
